import { TYPE_MENU } from 'constants/common';

/** Nhóm ứng dụng, như `GET /menus/groups` trả về (xếp theo position). */
export interface MenuGroup {
  id: number;
  name: string;
  position: number;
  menuCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Một ứng dụng (menu) trong `GET /menus/admin`. */
export interface MenuItem {
  id: number;
  name: string;
  link: string;
  icon: string;
  position: number;
  type: TYPE_MENU;
  groupId: number | null;
  group: Pick<MenuGroup, 'id' | 'name' | 'position'> | null;
  isPinned: boolean;
  /** App mới: hiện "Sao chép liên kết" ở nút ⓘ góc phải trang web của ứng dụng. */
  allowCopyLink: boolean;
  /** App mới: hiện "Mở bằng trình duyệt" ở nút ⓘ (trình duyệt cũng hiện link). */
  allowOpenBrowser: boolean;
  /** App mới: "Chỉ xem" — khoá hẳn trang (không sao chép, dán, bấm). */
  viewOnly: boolean;
  createdAt: string;
}

/**
 * Mức bảo vệ link trên app mới. Server lưu 3 cột (viewOnly, allowCopyLink,
 * allowOpenBrowser); admin chỉ chọn 1 trong 5 mức để khỏi ra tổ hợp vô nghĩa.
 */
export enum LINK_PROTECTION {
  VIEW_ONLY = 'VIEW_ONLY',
  HIDE_LINK = 'HIDE_LINK',
  ALLOW_COPY = 'ALLOW_COPY',
  ALLOW_BROWSER = 'ALLOW_BROWSER',
  ALLOW_BOTH = 'ALLOW_BOTH',
}

type LinkProtectionFields = Pick<MenuItem, 'viewOnly' | 'allowCopyLink' | 'allowOpenBrowser'>;

export const LINK_PROTECTION_OPTIONS: Array<{
  value: LINK_PROTECTION;
  /** Tên đầy đủ trong ô chọn của modal */
  label: string;
  /** Tên ngắn trên bảng */
  short: string;
  /** Giải thích dưới ô chọn */
  hint: string;
  fields: LinkProtectionFields;
}> = [
  {
    value: LINK_PROTECTION.VIEW_ONLY,
    label: 'Chỉ xem — khoá hoàn toàn',
    short: 'Chỉ xem',
    hint: 'Không có nút ⓘ, không chọn / sao chép / dán, không bấm được link hay nút nào trong trang. Người dùng chỉ cuộn, phóng to để xem và bấm quay lại. Không dùng cho Google Form (sẽ không điền được).',
    fields: { viewOnly: true, allowCopyLink: false, allowOpenBrowser: false },
  },
  {
    value: LINK_PROTECTION.HIDE_LINK,
    label: 'Ẩn link — vẫn bấm, điền được trong trang',
    short: 'Ẩn link',
    hint: 'Không có nút ⓘ nên không lấy được link, nhưng người dùng vẫn bấm và điền trong trang. Dùng cho Google Form.',
    fields: { viewOnly: false, allowCopyLink: false, allowOpenBrowser: false },
  },
  {
    value: LINK_PROTECTION.ALLOW_COPY,
    label: 'Cho sao chép liên kết',
    short: 'Cho sao chép',
    hint: 'Nút ⓘ chỉ có “Sao chép liên kết”.',
    fields: { viewOnly: false, allowCopyLink: true, allowOpenBrowser: false },
  },
  {
    value: LINK_PROTECTION.ALLOW_BROWSER,
    label: 'Cho mở bằng trình duyệt',
    short: 'Cho mở trình duyệt',
    hint: 'Nút ⓘ chỉ có “Mở bằng trình duyệt” — trình duyệt cũng hiện link trên thanh địa chỉ. Dùng khi trang bắt đăng nhập Google (Google chặn đăng nhập ngay trong app).',
    fields: { viewOnly: false, allowCopyLink: false, allowOpenBrowser: true },
  },
  {
    value: LINK_PROTECTION.ALLOW_BOTH,
    label: 'Cho sao chép và mở trình duyệt (như app cũ)',
    short: 'Cho cả hai',
    hint: 'Nút ⓘ có đủ “Sao chép liên kết” và “Mở bằng trình duyệt”.',
    fields: { viewOnly: false, allowCopyLink: true, allowOpenBrowser: true },
  },
];

/** Mức của một ứng dụng từ 3 cột server trả về (thiếu cột = false). */
export const linkProtectionOf = (menu?: Partial<LinkProtectionFields> | null): LINK_PROTECTION => {
  if (menu?.viewOnly) return LINK_PROTECTION.VIEW_ONLY;
  if (menu?.allowCopyLink && menu?.allowOpenBrowser) return LINK_PROTECTION.ALLOW_BOTH;
  if (menu?.allowCopyLink) return LINK_PROTECTION.ALLOW_COPY;
  if (menu?.allowOpenBrowser) return LINK_PROTECTION.ALLOW_BROWSER;
  return LINK_PROTECTION.HIDE_LINK;
};

/** 3 cột gửi lên server cho một mức. */
export const linkProtectionFields = (value: LINK_PROTECTION): LinkProtectionFields =>
  (LINK_PROTECTION_OPTIONS.find((option) => option.value === value) ?? LINK_PROTECTION_OPTIONS[1])
    .fields;

/**
 * Mã link mở màn có sẵn trong app (Chấm công, OT...) thay vì trang web — khớp
 * INTERNAL_SCREENS trong app. Các mục này không có nút ⓘ nên không cần ẩn link.
 */
export const INTERNAL_MENU_LINKS = [
  'TIMEKEEPING',
  'OVER_TIME',
  'APPROVED_OVERTIME',
  'LEAVE_REQUESTS',
];

export const isInternalMenuLink = (link?: string | null) =>
  INTERNAL_MENU_LINKS.includes((link || '').trim());

/**
 * Link trỏ thẳng vào file — giống hệt isDirectFileLink trong app (view-only-lock.ts).
 * Chỉ xét phần
 * đường dẫn (không xét tham số — docs.google.com/viewer?url=...pdf là trang
 * xem, không phải file) và các tham số tải/xuất quen thuộc.
 */
const FILE_EXT = /\.(pdf|docx?|xlsx?|pptx?|csv|tsv|odt|ods|odp|rtf|zip|rar|7z|apk|ipa)$/i;
const FILE_QUERY =
  /[?&](export=download|output=(pdf|xlsx|csv|tsv|ods|docx|zip)|format=(pdf|docx|xlsx|csv|tsv|pptx|ods|odt)|dl=1|download=1|alt=media)(&|$)/i;

export const isDirectFileLink = (link?: string | null) => {
  const match = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*([^?#]*)(\?[^#]*)?/i.exec((link || '').trim());
  if (!match) {
    return false;
  }
  const path = match[1] || '';
  const query = match[2] || '';
  return (
    FILE_EXT.test(path) ||
    FILE_QUERY.test(query) ||
    // Google: .../export?format=..., drive.google.com/uc?export=download
    (/\/(export|uc)$/i.test(path) && query.length > 0)
  );
};

/** Nhãn cho "Ai thấy"; giá trị gửi lên server vẫn là TYPE_MENU. */
export const MENU_TYPE_LABEL: Record<TYPE_MENU, string> = {
  [TYPE_MENU.ALL]: 'Tất cả',
  [TYPE_MENU.AGENCY]: 'Đối tác (Agency)',
  [TYPE_MENU.CREATOR]: 'Creator',
  [TYPE_MENU.HP_COMPANY]: 'Nhân sự HP',
  [TYPE_MENU.ADMIN]: 'Quản trị',
};

export const MENU_TYPE_OPTIONS = Object.values(TYPE_MENU).map((value) => ({
  value,
  label: MENU_TYPE_LABEL[value],
}));

/** Khớp MaxLength(64) của tên nhóm ở server. */
export const GROUP_NAME_MAX_LENGTH = 64;

/**
 * Câu báo lỗi tiếng Việt. Interceptor của AxiosUtils ném thẳng body lỗi của
 * server ({ statusCode, message }); mất mạng thì là lỗi JS thường, không có
 * statusCode. Lỗi 4xx có chi tiết thì kèm chi tiết trong ngoặc.
 */
export const getErrorMessage = (error: any, fallback: string): string => {
  const status = Number(error?.statusCode);
  if (status === 429) return 'Thao tác quá nhanh, vui lòng đợi vài giây rồi thử lại.';
  const detail = Array.isArray(error?.message) ? error.message.join('; ') : error?.message;
  if (status >= 400 && status < 500 && typeof detail === 'string' && detail.trim()) {
    return `${fallback} (${detail})`;
  }
  return fallback;
};
